import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { formatCurrency, formatPercentage } from '../utils/helpers';
import { 
  Sparkles, TrendingUp, TrendingDown, Loader2, LineChart, BarChart3, Brain,
  Calendar, Target, ArrowUpRight, ArrowDownRight, Sun, Cloud, Leaf, Snowflake,
  Clock, DollarSign, Users, ShoppingBag
} from 'lucide-react';
import {
  LineChart as RechartsLineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell, PieChart, Pie
} from 'recharts';

const TrendsForecastingPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [revenue, setRevenue] = useState([]);
  const [costs, setCosts] = useState([]);
  const [aiInsights, setAiInsights] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [forecastPeriod, setForecastPeriod] = useState('4');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 12); // Get 12 months of data
      const params = {
        start_date: startDate.toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0]
      };

      const [revRes, costRes] = await Promise.all([
        api.get('/revenue', { params }),
        api.get('/costs', { params })
      ]);
      setRevenue(revRes.data);
      setCosts(costRes.data);
    } catch (error) {
      toast.error('Fout bij laden data');
    } finally {
      setLoading(false);
    }
  };

  const generateAIInsights = async (module) => {
    setAiLoading(true);
    try {
      const context = {
        revenue: revenue.slice(-60),
        costs: costs.slice(-60),
        weekdayStats: weekdayData,
        seasonalStats: seasonalData,
        forecast: forecastData
      };

      const res = await api.post('/ai/insights', {
        module: module || 'forecast',
        context
      });
      setAiInsights(res.data.insights);
      toast.success('AI inzichten gegenereerd');
    } catch (error) {
      toast.error('Fout bij genereren AI inzichten');
    } finally {
      setAiLoading(false);
    }
  };

  // Day names in Dutch
  const dayNames = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'];
  const shortDayNames = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];
  const monthNames = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];

  // Process data for monthly chart
  const monthlyData = revenue.reduce((acc, r) => {
    const date = new Date(r.date);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const monthLabel = `${monthNames[date.getMonth()]} ${date.getFullYear().toString().slice(-2)}`;
    
    const existing = acc.find(a => a.monthKey === monthKey);
    if (existing) {
      existing.omzet += r.amount || 0;
      existing.gasten += r.guests || 0;
      existing.transacties += r.transactions || 0;
      existing.dagen += 1;
    } else {
      acc.push({ 
        monthKey, 
        month: monthLabel, 
        omzet: r.amount || 0, 
        kosten: 0,
        gasten: r.guests || 0,
        transacties: r.transactions || 0,
        dagen: 1
      });
    }
    return acc;
  }, []);

  // Add costs to monthly data
  costs.forEach(c => {
    const date = new Date(c.date);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const existing = monthlyData.find(a => a.monthKey === monthKey);
    if (existing) {
      existing.kosten += (c.food_cost || 0) + (c.beverage_cost || 0) + (c.personnel_cost || 0);
    }
  });

  // Sort by date and calculate margins
  monthlyData.sort((a, b) => a.monthKey.localeCompare(b.monthKey));
  monthlyData.forEach(m => {
    m.marge = m.omzet > 0 ? ((m.omzet - m.kosten) / m.omzet * 100) : 0;
    m.gemOmzetPerDag = m.dagen > 0 ? m.omzet / m.dagen : 0;
  });

  // Weekday analysis
  const weekdayData = dayNames.map((day, index) => ({
    dag: day,
    kort: shortDayNames[index],
    omzet: 0,
    gasten: 0,
    transacties: 0,
    count: 0
  }));

  revenue.forEach(r => {
    const dayIndex = new Date(r.date).getDay();
    weekdayData[dayIndex].omzet += r.amount || 0;
    weekdayData[dayIndex].gasten += r.guests || 0;
    weekdayData[dayIndex].transacties += r.transactions || 0;
    weekdayData[dayIndex].count += 1;
  });

  weekdayData.forEach(d => {
    d.gemOmzet = d.count > 0 ? d.omzet / d.count : 0;
    d.gemGasten = d.count > 0 ? d.gasten / d.count : 0;
  });

  // Find best and worst days
  const sortedDays = [...weekdayData].sort((a, b) => b.gemOmzet - a.gemOmzet);
  const bestDay = sortedDays[0];
  const worstDay = sortedDays[sortedDays.length - 1];

  // Calculate totals and averages
  const totalRevenue = revenue.reduce((sum, r) => sum + (r.amount || 0), 0);
  const totalCosts = costs.reduce((sum, c) => 
    sum + (c.food_cost || 0) + (c.beverage_cost || 0) + (c.personnel_cost || 0), 0);
  const totalGuests = revenue.reduce((sum, r) => sum + (r.guests || 0), 0);

  // Weekly revenue for forecasting
  const revenueByWeek = revenue.reduce((acc, r) => {
    const date = new Date(r.date);
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - date.getDay() + 1); // Start on Monday
    const weekKey = weekStart.toISOString().split('T')[0];
    
    if (!acc[weekKey]) {
      acc[weekKey] = { omzet: 0, gasten: 0, count: 0 };
    }
    acc[weekKey].omzet += r.amount || 0;
    acc[weekKey].gasten += r.guests || 0;
    acc[weekKey].count += 1;
    return acc;
  }, {});

  const weeklyRevenueArray = Object.entries(revenueByWeek)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([week, data]) => ({ week, ...data }));

  const avgWeeklyRevenue = weeklyRevenueArray.length > 0 
    ? weeklyRevenueArray.reduce((a, b) => a + b.omzet, 0) / weeklyRevenueArray.length 
    : 0;

  // Calculate trend (simple linear regression)
  const recentWeeks = weeklyRevenueArray.slice(-8);
  let trend = 0;
  if (recentWeeks.length >= 2) {
    const firstHalf = recentWeeks.slice(0, Math.floor(recentWeeks.length / 2));
    const secondHalf = recentWeeks.slice(Math.floor(recentWeeks.length / 2));
    const avgFirst = firstHalf.reduce((a, b) => a + b.omzet, 0) / firstHalf.length;
    const avgSecond = secondHalf.reduce((a, b) => a + b.omzet, 0) / secondHalf.length;
    trend = avgSecond - avgFirst;
  }

  // Forecast data
  const forecastWeeks = parseInt(forecastPeriod);
  const lastWeekRevenue = recentWeeks.length > 0 ? recentWeeks[recentWeeks.length - 1].omzet : avgWeeklyRevenue;
  const weeklyTrend = trend / (recentWeeks.length / 2);

  // Seasonal analysis - must be defined before forecast data uses it
  const seasonalData = [
    { seizoen: 'Winter', icon: Snowflake, color: '#60A5FA', omzet: 0, count: 0, months: [11, 0, 1] },
    { seizoen: 'Lente', icon: Leaf, color: '#34D399', omzet: 0, count: 0, months: [2, 3, 4] },
    { seizoen: 'Zomer', icon: Sun, color: '#FBBF24', omzet: 0, count: 0, months: [5, 6, 7] },
    { seizoen: 'Herfst', icon: Cloud, color: '#F97316', omzet: 0, count: 0, months: [8, 9, 10] }
  ];

  revenue.forEach(r => {
    const month = new Date(r.date).getMonth();
    const season = seasonalData.find(s => s.months.includes(month));
    if (season) {
      season.omzet += r.amount || 0;
      season.count += 1;
    }
  });

  seasonalData.forEach(s => {
    s.gemiddeld = s.count > 0 ? s.omzet / s.count : 0;
  });

  // Forecast data - uses seasonalData for seasonal adjustments
  const forecastData = [];
  for (let i = 1; i <= forecastWeeks; i++) {
    const forecastDate = new Date();
    forecastDate.setDate(forecastDate.getDate() + (i * 7));
    const weekNum = Math.ceil((forecastDate - new Date(forecastDate.getFullYear(), 0, 1)) / (7 * 24 * 60 * 60 * 1000));
    
    // Apply seasonality factor based on historical data
    const monthIndex = forecastDate.getMonth();
    const seasonFactor = seasonalData.find(s => {
      if (s.seizoen === 'Winter' && (monthIndex === 11 || monthIndex === 0 || monthIndex === 1)) return true;
      if (s.seizoen === 'Lente' && monthIndex >= 2 && monthIndex <= 4) return true;
      if (s.seizoen === 'Zomer' && monthIndex >= 5 && monthIndex <= 7) return true;
      if (s.seizoen === 'Herfst' && monthIndex >= 8 && monthIndex <= 10) return true;
      return false;
    });
    
    const baseforecast = lastWeekRevenue + (weeklyTrend * i);
    const seasonalAdjustment = seasonFactor && avgWeeklyRevenue > 0 
      ? (seasonFactor.gemiddeld / (avgWeeklyRevenue / 7)) 
      : 1;
    
    forecastData.push({
      week: `W${weekNum}`,
      weekNum: i,
      voorspelling: Math.max(0, baseforecast * (seasonalAdjustment > 0 ? Math.min(1.5, Math.max(0.5, seasonalAdjustment)) : 1)),
      gemiddeld: avgWeeklyRevenue,
      optimistisch: Math.max(0, baseforecast * 1.15),
      pessimistisch: Math.max(0, baseforecast * 0.85),
      datum: forecastDate.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })
    });
  }

  const bestSeason = [...seasonalData].sort((a, b) => b.gemiddeld - a.gemiddeld)[0];

  // Year-over-year comparison
  const currentYear = new Date().getFullYear();
  const lastYear = currentYear - 1;
  
  const thisYearRevenue = revenue.filter(r => new Date(r.date).getFullYear() === currentYear)
    .reduce((sum, r) => sum + (r.amount || 0), 0);
  const lastYearRevenue = revenue.filter(r => new Date(r.date).getFullYear() === lastYear)
    .reduce((sum, r) => sum + (r.amount || 0), 0);
  
  const yoyChange = lastYearRevenue > 0 ? ((thisYearRevenue - lastYearRevenue) / lastYearRevenue * 100) : 0;

  // Benchmark data for pie chart
  const benchmarkData = [
    { name: 'Food Cost', value: costs.reduce((s, c) => s + (c.food_cost || 0), 0), color: '#C2410C' },
    { name: 'Beverage Cost', value: costs.reduce((s, c) => s + (c.beverage_cost || 0), 0), color: '#D97706' },
    { name: 'Personeel', value: costs.reduce((s, c) => s + (c.personnel_cost || 0), 0), color: '#1A2E05' },
    { name: 'Overig', value: costs.reduce((s, c) => s + (c.other_costs || 0), 0), color: '#57534E' }
  ].filter(b => b.value > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#C2410C]" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fade-in" data-testid="trends-forecasting-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
            Trends & Forecasting
          </h1>
          <p className="text-[#57534E] mt-1">
            Analyseer trends, patronen en krijg omzet voorspellingen
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            className="btn-primary"
            onClick={() => generateAIInsights('forecast')}
            disabled={aiLoading}
            data-testid="generate-ai-btn"
          >
            {aiLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Analyseren...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                AI Analyse
              </>
            )}
          </Button>
        </div>
      </div>

      {/* AI Insights */}
      {aiInsights && (
        <Card className="card-base border-2 border-[#C2410C]/20 bg-gradient-to-br from-[#C2410C]/5 to-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Brain className="w-5 h-5 text-[#C2410C]" />
              AI Inzichten & Aanbevelingen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              <div className="whitespace-pre-wrap text-[#1C1917]">{aiInsights}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="kpi-tile">
          <p className="kpi-label">Totale Omzet (12 mnd)</p>
          <p className="kpi-value">{formatCurrency(totalRevenue)}</p>
          <div className={`flex items-center gap-1 text-sm ${yoyChange >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
            {yoyChange >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            {yoyChange >= 0 ? '+' : ''}{yoyChange.toFixed(1)}% vs vorig jaar
          </div>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Gem. Week Omzet</p>
          <p className="kpi-value">{formatCurrency(avgWeeklyRevenue)}</p>
          <p className="text-sm text-[#57534E]">{weeklyRevenueArray.length} weken data</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Week Trend</p>
          <p className={`kpi-value flex items-center gap-1 ${trend >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
            {trend >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
            {trend >= 0 ? '+' : ''}{formatCurrency(trend)}
          </p>
          <p className="text-sm text-[#57534E]">groei per week</p>
        </div>
        <div className="kpi-tile border-2 border-emerald-500/20 bg-emerald-50/50">
          <p className="kpi-label text-emerald-700">Beste Dag</p>
          <p className="kpi-value text-emerald-600">{bestDay?.dag}</p>
          <p className="text-sm text-emerald-700">gem. {formatCurrency(bestDay?.gemOmzet || 0)}</p>
        </div>
        <div className="kpi-tile border-2 border-amber-500/20 bg-amber-50/50">
          <p className="kpi-label text-amber-700">Beste Seizoen</p>
          <p className="kpi-value text-amber-600">{bestSeason?.seizoen}</p>
          <p className="text-sm text-amber-700">gem. {formatCurrency(bestSeason?.gemiddeld || 0)}/dag</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overzicht</TabsTrigger>
          <TabsTrigger value="forecast">Forecast</TabsTrigger>
          <TabsTrigger value="weekdays">Weekdagen</TabsTrigger>
          <TabsTrigger value="seasonal">Seizoenen</TabsTrigger>
          <TabsTrigger value="benchmarks">Benchmarks</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue & Cost Development */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Omzet & Kosten Ontwikkeling</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={monthlyData}>
                      <defs>
                        <linearGradient id="colorOmzet" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#C2410C" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#C2410C" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorKosten" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1A2E05" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#1A2E05" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#57534E' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#57534E' }} tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Area type="monotone" dataKey="omzet" name="Omzet" stroke="#C2410C" strokeWidth={2} fill="url(#colorOmzet)" />
                      <Area type="monotone" dataKey="kosten" name="Kosten" stroke="#1A2E05" strokeWidth={2} fill="url(#colorKosten)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Margin Development */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Marge Ontwikkeling</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsLineChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#57534E' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#57534E' }} tickFormatter={(v) => `${v.toFixed(0)}%`} domain={[0, 100]} />
                      <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                      <Line type="monotone" dataKey="marge" name="Marge %" stroke="#15803D" strokeWidth={3} dot={{ fill: '#15803D' }} />
                    </RechartsLineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Performance Cards */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}>Maandelijkse Prestaties</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {monthlyData.slice(-6).map((month, idx) => {
                  const prevMonth = monthlyData[monthlyData.indexOf(month) - 1];
                  const change = prevMonth ? ((month.omzet - prevMonth.omzet) / prevMonth.omzet * 100) : 0;
                  const isCurrentMonth = idx === monthlyData.slice(-6).length - 1;
                  
                  return (
                    <div 
                      key={month.monthKey}
                      className={`p-4 rounded-xl border ${isCurrentMonth ? 'border-2 border-[#C2410C] bg-[#C2410C]/5' : 'border-stone-200 bg-stone-50'}`}
                    >
                      <p className={`text-sm font-medium ${isCurrentMonth ? 'text-[#C2410C]' : 'text-[#57534E]'}`}>
                        {month.month}
                      </p>
                      <p className={`text-lg font-bold mt-1 ${isCurrentMonth ? 'text-[#C2410C]' : 'text-[#1C1917]'}`}>
                        {formatCurrency(month.omzet)}
                      </p>
                      {change !== 0 && (
                        <p className={`text-xs mt-1 flex items-center gap-1 ${change >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {change >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                          {change >= 0 ? '+' : ''}{change.toFixed(1)}%
                        </p>
                      )}
                      <p className="text-xs text-[#78716C] mt-2">Marge: {month.marge.toFixed(1)}%</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Forecast Tab */}
        <TabsContent value="forecast" className="mt-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[#1C1917]">Omzet Voorspelling</h3>
            <Select value={forecastPeriod} onValueChange={setForecastPeriod}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Forecast periode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="4">4 weken vooruit</SelectItem>
                <SelectItem value="8">8 weken vooruit</SelectItem>
                <SelectItem value="12">12 weken vooruit</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Forecast Chart */}
            <Card className="card-base lg:col-span-2">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Forecast Komende Weken</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={forecastData}>
                      <defs>
                        <linearGradient id="colorForecast" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#C2410C" stopOpacity={0.2}/>
                          <stop offset="95%" stopColor="#C2410C" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#57534E' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#57534E' }} tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                      <Tooltip 
                        formatter={(value) => formatCurrency(value)}
                        labelFormatter={(label, payload) => payload[0]?.payload?.datum || label}
                      />
                      <Legend />
                      <Area type="monotone" dataKey="optimistisch" name="Optimistisch" stroke="#15803D" strokeDasharray="3 3" fill="none" />
                      <Area type="monotone" dataKey="voorspelling" name="Voorspelling" stroke="#C2410C" strokeWidth={3} fill="url(#colorForecast)" />
                      <Area type="monotone" dataKey="pessimistisch" name="Pessimistisch" stroke="#DC2626" strokeDasharray="3 3" fill="none" />
                      <Line type="monotone" dataKey="gemiddeld" name="Historisch Gem." stroke="#57534E" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Forecast Details */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Verwachte Omzet</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {forecastData.map((week, idx) => (
                  <div 
                    key={week.week}
                    className={`p-3 rounded-lg border ${idx === 0 ? 'border-[#C2410C] bg-[#C2410C]/5' : 'border-stone-200 bg-stone-50'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-[#1C1917]">{week.week}</p>
                        <p className="text-xs text-[#57534E]">{week.datum}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${idx === 0 ? 'text-[#C2410C]' : 'text-[#1C1917]'}`}>
                          {formatCurrency(week.voorspelling)}
                        </p>
                        <p className="text-xs text-[#57534E]">
                          ±{formatCurrency(week.optimistisch - week.voorspelling)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
                
                <div className="pt-4 border-t border-stone-200">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-[#1C1917]">Totaal Forecast</p>
                    <p className="text-xl font-bold text-[#C2410C]">
                      {formatCurrency(forecastData.reduce((s, w) => s + w.voorspelling, 0))}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Weekdays Tab */}
        <TabsContent value="weekdays" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Weekday Chart */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Omzet per Weekdag</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={weekdayData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis dataKey="kort" tick={{ fontSize: 12, fill: '#57534E' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#57534E' }} tickFormatter={(v) => `€${v}`} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="gemOmzet" name="Gem. Omzet" radius={[4, 4, 0, 0]}>
                        {weekdayData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.dag === bestDay?.dag ? '#C2410C' : entry.dag === worstDay?.dag ? '#DC2626' : '#1A2E05'} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Weekday Details */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Weekdag Analyse</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {sortedDays.map((day, idx) => (
                  <div 
                    key={day.dag}
                    className={`p-3 rounded-lg border flex items-center justify-between ${
                      idx === 0 ? 'border-emerald-500 bg-emerald-50' : 
                      idx === sortedDays.length - 1 ? 'border-rose-300 bg-rose-50' : 
                      'border-stone-200 bg-stone-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                        idx === 0 ? 'bg-emerald-500 text-white' : 
                        idx === sortedDays.length - 1 ? 'bg-rose-400 text-white' : 
                        'bg-stone-300 text-stone-700'
                      }`}>
                        #{idx + 1}
                      </div>
                      <div>
                        <p className="font-semibold text-[#1C1917]">{day.dag}</p>
                        <p className="text-xs text-[#57534E]">{day.count} dagen data</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${idx === 0 ? 'text-emerald-600' : idx === sortedDays.length - 1 ? 'text-rose-600' : 'text-[#1C1917]'}`}>
                        {formatCurrency(day.gemOmzet)}
                      </p>
                      <p className="text-xs text-[#57534E]">gem. {Math.round(day.gemGasten)} gasten</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Seasonal Tab */}
        <TabsContent value="seasonal" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {seasonalData.map((season, idx) => {
              const Icon = season.icon;
              const isBest = season.seizoen === bestSeason?.seizoen;
              return (
                <Card 
                  key={season.seizoen}
                  className={`card-base ${isBest ? 'border-2 border-amber-500 bg-gradient-to-br from-amber-50 to-white' : ''}`}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${season.color}20` }}
                      >
                        <Icon className="w-6 h-6" style={{ color: season.color }} />
                      </div>
                      <div>
                        <p className="font-bold text-[#1C1917]">{season.seizoen}</p>
                        {isBest && <Badge className="bg-amber-500 text-white">Beste</Badge>}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-[#57534E]">Totale omzet</span>
                        <span className="font-bold" style={{ color: season.color }}>{formatCurrency(season.omzet)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-[#57534E]">Gem. per dag</span>
                        <span className="font-medium">{formatCurrency(season.gemiddeld)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-[#57534E]">Dagen data</span>
                        <span className="font-medium">{season.count}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Seasonal Chart */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}>Seizoensvergelijking</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={seasonalData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                    <XAxis type="number" tick={{ fontSize: 11, fill: '#57534E' }} tickFormatter={(v) => `€${v}`} />
                    <YAxis dataKey="seizoen" type="category" tick={{ fontSize: 12, fill: '#57534E' }} width={80} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="gemiddeld" name="Gem. per dag" radius={[0, 4, 4, 0]}>
                      {seasonalData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Benchmarks Tab */}
        <TabsContent value="benchmarks" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Cost Distribution Pie */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Kostenverdeling</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={benchmarkData}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={{ stroke: '#57534E' }}
                      >
                        {benchmarkData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Key Metrics */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Belangrijke Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-stone-50 rounded-xl border border-stone-200">
                  <div className="flex items-center gap-3 mb-2">
                    <DollarSign className="w-5 h-5 text-[#C2410C]" />
                    <span className="font-semibold">Bruto Marge</span>
                  </div>
                  <p className="text-2xl font-bold text-[#1C1917]">
                    {totalRevenue > 0 ? formatPercentage((totalRevenue - totalCosts) / totalRevenue * 100) : '0%'}
                  </p>
                  <p className="text-sm text-[#57534E]">Benchmark horeca: 60-70%</p>
                </div>

                <div className="p-4 bg-stone-50 rounded-xl border border-stone-200">
                  <div className="flex items-center gap-3 mb-2">
                    <ShoppingBag className="w-5 h-5 text-[#C2410C]" />
                    <span className="font-semibold">Food Cost %</span>
                  </div>
                  <p className="text-2xl font-bold text-[#1C1917]">
                    {totalRevenue > 0 ? formatPercentage(costs.reduce((s, c) => s + (c.food_cost || 0), 0) / totalRevenue * 100) : '0%'}
                  </p>
                  <p className="text-sm text-[#57534E]">Benchmark horeca: 25-35%</p>
                </div>

                <div className="p-4 bg-stone-50 rounded-xl border border-stone-200">
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="w-5 h-5 text-[#C2410C]" />
                    <span className="font-semibold">Personeel Cost %</span>
                  </div>
                  <p className="text-2xl font-bold text-[#1C1917]">
                    {totalRevenue > 0 ? formatPercentage(costs.reduce((s, c) => s + (c.personnel_cost || 0), 0) / totalRevenue * 100) : '0%'}
                  </p>
                  <p className="text-sm text-[#57534E]">Benchmark horeca: 30-40%</p>
                </div>

                <div className="p-4 bg-stone-50 rounded-xl border border-stone-200">
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="w-5 h-5 text-[#C2410C]" />
                    <span className="font-semibold">Gem. Besteding per Gast</span>
                  </div>
                  <p className="text-2xl font-bold text-[#1C1917]">
                    {totalGuests > 0 ? formatCurrency(totalRevenue / totalGuests) : '€0'}
                  </p>
                  <p className="text-sm text-[#57534E]">{totalGuests.toLocaleString()} gasten totaal</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TrendsForecastingPage;
